import { Checkbox, Divider, Icon, Menu, MenuItem, Popover, Tooltip } from '@snpjs/core';

import { ABAPBoolean } from '../../../shared/types/ABAPBoolean';
import { ConflictList } from '../../conflictManagement/conflictList/conflictList/ConflictList';
import { EditableCard } from '../../../components/editableCard/EditableCard';
import { IMenuItem } from './IMenuItem';
import { ISystemCardProps } from './ISystemCardProps';
import { LoadingSpinnerButton } from '../../../components/loadingSpinnerButton/LoadingSpinnerButton';
import { MessageArea } from '../../../components/messageArea/MessageArea';
import { MultiSelection } from '../../../components/selection/multiSelection/MultiSelection';
import { MultiSelectionDropDown } from '../../../components/selection/multiSelectionDropDown/MultiSelectionDropDown';
import { ReactElement } from 'react';
import React from 'react';
import { SystemDetailsOverviewPopover } from '../systemDetailsOverviewPopover/SystemDetailsOverviewPopover';
import { SystemDownloadHistory } from '../systemDownloadHistory/SystemDownloadHistory';
import { SystemDummy } from '../../../model/SystemDummy';
import { SystemHeaderFields } from '../systemHeaderFields/SystemHeaderFields';
import { SystemRequirementTypeList } from '../systemRequirementTypeList/SystemRequirementTypeList';
import { TagElement } from '../../../components/tagElement/TagElement';
import styles from './SystemCard.module.scss';
import { texts } from '../../../i18n/texts';
import { useConfig } from '../../../hooks/useConfig';
import { useRequestEmergencyLicenseCommand } from '../systemCommands/useRequestEmergencyLicenseCommand';
import { useSetSystemLandscapeDownloadVersionCommand } from '../systemCommands/useSetSystemLandscapeDownloadVersionCommand';
import { useSetSystemLandscapeUpgradeVersionCommand } from '../systemCommands/useSetSystemLandscapeUpgradeVersionCommand';
import { useSystemCardViewModel } from './useSystemCardViewModel';
import useTranslation from '../../../hooks/useTranslation';

export const SystemCard: React.FC<ISystemCardProps> = (props) => {
	const viewModel = useSystemCardViewModel(props);
	const { t } = useTranslation();
	const config = useConfig();

	const [isDownloadPopoverOpen, setDownloadPopoverOpen] = React.useState(false);
	const [isHelpPopoverOpen, setHelpPopoverOpen] = React.useState(false);

	const [isHoveringDownloadButton, setHoveringDownloadButton] =
		React.useState(false);
	const [isHoveringDownloadMenu, setHoveringDownloadMenu] =
		React.useState(false);

	const [isHoveringHelpButton, setHoveringHelpButton] = React.useState(false);
	const [isHoveringHelpMenu, setHoveringHelpMenu] = React.useState(false);

	const hoverCloseTimeout = React.useRef<number | null>(null);
	const hoverCloseTimeoutHelp = React.useRef<number | null>(null);

	React.useEffect(() => {
		if (isHoveringHelpButton || isHoveringHelpMenu) {
			if (hoverCloseTimeoutHelp.current) {
				window.clearTimeout(hoverCloseTimeoutHelp.current);
				hoverCloseTimeoutHelp.current = null;
			}
			setHelpPopoverOpen(true);
			return;
		}

		hoverCloseTimeoutHelp.current = window.setTimeout(() => {
			setHelpPopoverOpen(false);
			hoverCloseTimeoutHelp.current = null;
		}, 150);

		return () => {
			if (hoverCloseTimeoutHelp.current) {
				window.clearTimeout(hoverCloseTimeoutHelp.current);
				hoverCloseTimeoutHelp.current = null;
			}
		};
	}, [isHoveringHelpButton, isHoveringHelpMenu]);
	React.useEffect(() => {
		if (isHoveringDownloadButton || isHoveringDownloadMenu) {
			if (hoverCloseTimeout.current) {
				window.clearTimeout(hoverCloseTimeout.current);
				hoverCloseTimeout.current = null;
			}
			setDownloadPopoverOpen(true);
			return;
		}
		hoverCloseTimeout.current = window.setTimeout(() => {
			setDownloadPopoverOpen(false);
			hoverCloseTimeout.current = null;
		}, 150);

		return () => {
			if (hoverCloseTimeout.current) {
				window.clearTimeout(hoverCloseTimeout.current);
				hoverCloseTimeout.current = null;
			}
		};
	}, [isHoveringDownloadButton, isHoveringDownloadMenu]);

	React.useEffect(() => {
		function handleScroll() {
			if (isDownloadPopoverOpen) setDownloadPopoverOpen(false);
			if (isHelpPopoverOpen) setHelpPopoverOpen(false);
			setHoveringDownloadButton(false);
			setHoveringDownloadMenu(false);
			setHoveringHelpButton(false);
			setHoveringHelpMenu(false);
		}
		if (isDownloadPopoverOpen || isHelpPopoverOpen) {
			window.addEventListener('scroll', handleScroll, true);
			return () => window.removeEventListener('scroll', handleScroll, true);
		}
	}, [isDownloadPopoverOpen, isHelpPopoverOpen]);

	const messageAreaContent = viewModel.message && (
		<div>
			<MessageArea message={viewModel.message} />
		</div>
	);

	const downloadVersionCommand = useSetSystemLandscapeDownloadVersionCommand({
		productId: props.product.OBJECT_ID,
		isVersionChangeable: true,
		systemLandscape: props.systemLandscape,
		onOkay: props.onDownloadVersion,
	});

	const upgradeVersionCommand = useSetSystemLandscapeUpgradeVersionCommand({
		productId: props.product.OBJECT_ID,
		isVersionChangeable: true,
		systemLandscape: props.systemLandscape,
		release: props.systemLandscape.RELEASE,
		isLTS: props.systemLandscape.IS_LTS,
		servicePack: props.systemLandscape.SERVICE_PACK,
		hotfix: props.systemLandscape.HOTFIX,
		onOkay: props.onUpgradeVersion,
	});

	const hotfixAvailable = props.system.IS_HOTFIX_AVAILABLE === ABAPBoolean.true;

	const onDownloadVersionCommand = () => {
		downloadVersionCommand.execute();
	};

	const onUpgradeVersionCommand = () => {
		upgradeVersionCommand.execute();
	};

	const onRequestEmergencyLicense = async (reason: string) => {
		try {
			await viewModel.onDownloadEmergencyLicense(reason);
			return true;
		} catch (error) {
			return false;
		}
	};

	const EmergencyLicenseCommand = useRequestEmergencyLicenseCommand({
		//Nachdem einmal requested wurde, kann man sie einfach herunterladen --> das Popup soll also nicht mehr geladen werden!
		onOkay: onRequestEmergencyLicense,
		productId: props.product.PRODUCT_ID,
		getNewEmergencyExpirationDate: viewModel.getNewEmergencyExpirationDate,
		emergencyLicenseDuration: config.EMERGENCY_EXT_DAYS,
	});

	const downloadSoftwareButtonText =
		t(texts.systemCard.downloadSoftwareButton) +
		' ' +
		props.systemLandscape.RELEASE +
		' SP ' +
		props.systemLandscape.SERVICE_PACK +
		' HF ' +
		props.systemLandscape.HOTFIX;

	const downloadSoftwareBundleButtonText = t(
		texts.systemCard.downloadSoftwareBundleButton
	);

	const downloadSoftwareBundleHotfixesButtonText = t(
		texts.systemCard.downloadSoftwareHotfixesButton
	);

	const onHelpButtonMouseEnter = () => {
		setHoveringHelpButton(true);
		if (
			viewModel.installationHelpMenuEntries.length === 0 &&
			!viewModel.isLoadingDocu
		) {
			viewModel.onLoadInstallationHelpMenu();
		}
	};

	const downloadMenu = (
		<Menu
			onMouseEnter={() => setHoveringDownloadMenu(true)}
			onMouseLeave={() => setHoveringDownloadMenu(false)}
		>
			{viewModel.isLicenseActive ? (
				<>
					<MenuItem
						icon='download'
						disabled={false}
						text={downloadSoftwareButtonText}
					>
						<MenuItem
							icon='download'
							disabled={false}
							onClick={viewModel.onDownloadSoftwareBundle}
							text={downloadSoftwareBundleButtonText}
						/>
						<MenuItem
							icon='download'
							disabled={!hotfixAvailable}
							onClick={viewModel.onDownloadSoftwareBundleHotfixes}
							text={downloadSoftwareBundleHotfixesButtonText}
						/>
					</MenuItem>
				</>
			) : (
				<>
					<Tooltip
						className={styles.elementWithTooltip}
						content={t(texts.systemCard.tooltipDownloadExpired)}
					>
						<MenuItem
							icon='download'
							disabled={true}
							text={downloadSoftwareButtonText}
						>
							<Tooltip
								className={styles.elementWithTooltip}
								content={t(texts.systemCard.tooltipDownloadExpired)}
							>
								<MenuItem
									icon='package'
									disabled={true}
									onClick={viewModel.onDownloadSoftwareBundle}
									text={downloadSoftwareBundleButtonText}
								/>
							</Tooltip>
							<Tooltip
								className={styles.elementWithTooltip}
								content={t(texts.systemCard.tooltipDownloadExpired)}
							>
								<MenuItem
									icon='package'
									disabled={true}
									onClick={viewModel.onDownloadSoftwareBundleHotfixes}
									text={downloadSoftwareBundleHotfixesButtonText}
								/>
							</Tooltip>
						</MenuItem>
					</Tooltip>
				</>
			)}
			{!viewModel.isLicenseRequired() ? (
				<Tooltip
					className={styles.elementWithTooltip}
					content={t(texts.systemCard.tooltipNotLicensable)}
				>
					<MenuItem
						icon='identification-card'
						disabled={true}
						onClick={viewModel.onDownloadLicense}
						text={t(texts.systemCard.downloadLicenseButton)}
					/>
				</Tooltip>
			) : !viewModel.isLicenseActive ? (
				<Tooltip
					className={styles.elementWithTooltip}
					content={t(texts.systemCard.tooltipDownloadExpired)}
				>
					<MenuItem
						icon='identification-card'
						disabled={true}
						onClick={viewModel.onDownloadLicense}
						text={t(texts.systemCard.downloadLicenseButton)}
					/>
				</Tooltip>
			) : (
				<MenuItem
					icon='identification-card'
					disabled={viewModel.isLicenseDownloadDisabled}
					onClick={viewModel.onDownloadLicense}
					text={t(texts.systemCard.downloadLicenseButton)}
				/>
			)}

			{(viewModel.isExpired() || viewModel.isAboutToExpire()) && (
				<div>
					<Divider className={styles.divider} />
					{viewModel.isEmergencyLicenseRequestable() ? (
						<MenuItem
							icon={<Icon icon='first-aid-kit' intent='danger' />}
							disabled={false}
							onClick={() => {
								EmergencyLicenseCommand.execute();
							}}
							text={t(texts.systemCard.downloadEmergencyLicenseButton)}
						/>
					) : (
						<Tooltip
							className={styles.elementWithTooltip}
							content={t(texts.systemCard.alreadyRunningEmergencyLicense)}
						>
							<MenuItem
								icon={<Icon icon='first-aid-kit' intent='danger' />}
								disabled={true}
								onClick={() => {
									EmergencyLicenseCommand.execute();
								}}
								text={t(texts.systemCard.downloadEmergencyLicenseButton)}
							/>
						</Tooltip>
					)}
				</div>
			)}

			{props.canVersionBeAdjusted &&
				props.systemLandscape.RELEASE.toString() !== '0000' && (
					<Tooltip
						className={styles.elementWithTooltip}
						content={t(texts.systemLandscape.captionAdjustVersion)}
					>
						<MenuItem
							icon='git-pull-request'
							onClick={onDownloadVersionCommand}
							text={t(texts.systemLandscape.captionAdjustVersion)}
						/>
					</Tooltip>
				)}
			{!props.canVersionBeAdjusted && props.canBeUpgraded && (
				<Tooltip
					className={styles.elementWithTooltip}
					content={t(texts.systemLandscape.captionUpgradeVersion)}
				>
					<MenuItem
						icon='git-merge'
						onClick={onUpgradeVersionCommand}
						text={t(texts.systemLandscape.captionUpgradeVersion)}
					/>
				</Tooltip>
			)}
		</Menu>
	);

	const buildMenuItems = (menuItem: IMenuItem, index: number): ReactElement => {
		return (
			<MenuItem
				key={index}
				text={menuItem.text}
				icon={menuItem.icon}
				onClick={menuItem.onClick}
				children={menuItem.children?.map((child, childIndex) =>
					buildMenuItems(child, childIndex)
				)}
			></MenuItem>
		);
	};

	const helpMenu = (
		<Menu
			onMouseEnter={() => setHoveringHelpMenu(true)}
			onMouseLeave={() => setHoveringHelpMenu(false)}
		>
			{viewModel.installationHelpMenuEntries.map((entry, index) =>
				buildMenuItems(entry, index)
			)}
		</Menu>
	);

	const showSystemRequirements = props.systemRequirementTypes.length > 0;
	return (
		<EditableCard
			actionsDisplayMode={
				<>
					{EmergencyLicenseCommand.view}
						<Tooltip
							content={
								viewModel.isDownloading
									? t(texts.systemCard.tooltipDownloadBundling)
									: t(texts.systemCard.tooltipDownload)
							}
						>
							<Popover
								content={downloadMenu}
								placement='bottom-start'
								isOpen={isDownloadPopoverOpen}
								onOpened={() => setDownloadPopoverOpen(true)}
								onClose={() => {
									setDownloadPopoverOpen(false);
									setHoveringDownloadButton(false);
									setHoveringDownloadMenu(false);
									if (hoverCloseTimeout.current) {
										window.clearTimeout(hoverCloseTimeout.current);
										hoverCloseTimeout.current = null;
									}
								}}
							>
								<LoadingSpinnerButton
									disabled={
										viewModel.isSystemDeactivated ||
										viewModel.hasSystemConflicts ||
										viewModel.isSystemRoleSelectionInvalid() ||
										viewModel.isSystemRequirementSelectionInvalid() ||
										props.containsSystemVersionInconsistency
									}
									icon={<Icon icon={'download'} />}
									outlined
									showSpinner={viewModel.isDownloading}
									spinnerClassName={styles.spinner}
									onMouseEnter={() => setHoveringDownloadButton(true)}
									onMouseLeave={() => setHoveringDownloadButton(false)}
								/>
							</Popover>
						</Tooltip>

					<Tooltip content={t(texts.systemCard.tooltipInstallHelp)}>
						<Popover
							content={helpMenu}
							placement='bottom-start'
							isOpen={isHelpPopoverOpen}
							onOpened={() => setHelpPopoverOpen(true)}
							onClose={() => {
								setHelpPopoverOpen(false);
								setHoveringHelpButton(false);
								setHoveringHelpMenu(false);
								if (hoverCloseTimeoutHelp.current) {
									window.clearTimeout(hoverCloseTimeoutHelp.current);
									hoverCloseTimeoutHelp.current = null;
								}
							}}
						>
							<LoadingSpinnerButton
								showSpinner={viewModel.isLoadingDocu}
								outlined
								icon={<Icon icon={'book-info'} />}
								spinnerClassName={styles.spinner}
								onClick={viewModel.onLoadInstallationHelpMenu}
								onMouseEnter={onHelpButtonMouseEnter}
								onMouseLeave={() => setHoveringHelpButton(false)}
							/>
						</Popover>
					</Tooltip>
				</>
			}
			actionsChangeMode={
				<MenuItem
					disabled={viewModel.isSystemDeactivated}
					text={t(texts.systemCard.labelMarkAsUnused)}
					icon='envelope-simple-open'
					onClick={viewModel.systemDeactivateCommand.execute}
				/>
			}
			deleteText={(system) => {
				return {
					text: texts.systemCard.deleteText,
					placeholders: {
						installNumber: system.INSTNUMBER,
					},
				};
			}}
			deleteTitle={texts.systemCard.deleteTitle}
			dummyClassType={SystemDummy}
			editMode={viewModel.editMode}
			hideActions={viewModel.needsCollapse}
			initialObject={props.system}
			onRestore={viewModel.onRestore}
			onCopy={viewModel.onCopy}
			onDelete={viewModel.onDelete}
			onSave={viewModel.onSave}
			onValidate={viewModel.onValidate}
			setEditMode={viewModel.setEditMode}
			setObject={viewModel.setSystem}
		>
			{downloadVersionCommand.view}
			{upgradeVersionCommand.view}
			{viewModel.systemDeactivateCommand.view}
			<div className={styles.systemCard}>
				{messageAreaContent}
				<div className={styles.detailsHeader}>
					<div className={styles.systemHeaderFields}>
							<div className={styles.systemSelectionControls}>
								{!viewModel.editMode && (
									<div className={styles.collapseIcon}>
										<Icon
											className={styles.collapseIcon}
											icon={viewModel.needsCollapse ? 'caret-down' : 'caret-up'}
											onClick={viewModel.toggleIsCollapsed}
										/>
									</div>
								)}
								{props.showSelectionCheckbox && (
									<Checkbox
										checked={props.isSelectedForClipboard}
										onChange={(event) =>
											props.onToggleSelectedForClipboard?.(event.target.checked)
										}
									/>
								)}
							</div>
						<SystemHeaderFields
							inputFieldErrorClient={viewModel.inputFieldErrorClient}
							inputFieldErrorInstallNumber={
								viewModel.inputFieldErrorInstallNumber
							}
							inputFieldErrorSid={viewModel.inputFieldErrorSid}
							inputFieldErrorDescription={viewModel.inputFieldErrorDescription}
							inputFieldErrorTotalSystemsCount={
								viewModel.inputFieldErrorTotalSystemsCount
							}
							inputFieldErrorProductionSystemsCount={
								viewModel.inputFieldErrorProductionSystemsCount
							}
							showZeroSystemsWarning={
								!viewModel.needsCollapse && viewModel.showZeroSystemsWarning
							}
							editMode={viewModel.editMode}
							onSetSid={viewModel.onSetSid}
							system={viewModel.system}
							onSetClient={viewModel.onSetClient}
							onSetInstallNumber={viewModel.onSetInstallNumber}
							onSetDescription={viewModel.onSetDescription}
							onSetTotalSystemsCount={viewModel.onSetTotalSystemsCount}
							onSetProductionSystemsCount={
								viewModel.onSetProductionSystemsCount
							}
							showTotalSystemsCount={viewModel.showTotalSystemsCount}
							showProductionSystemsCount={viewModel.showProductionSystemsCount}
						/>
						<div className={styles.systemDetails}>
							<TagElement text={props.number.toString()} />
							{SystemDummy.isPersistent(props.system) && (
								<SystemDetailsOverviewPopover
									systemObjectId={viewModel.system.OBJECT_ID}
								/>
							)}
						</div>
						{viewModel.needsCollapse &&
							props.systemRoles
								.map((role) => viewModel.isSystemRoleSelected(role))
								.includes(true) && (
								<div className={styles.systemRoleInfo}>
									<h4 className={styles.collapsedRoleHeader}>
										{t(texts.systemCard.systemRoles)}
									</h4>
									<MultiSelection
										editMode={viewModel.editMode}
										getName={(systemRole) => systemRole.DESCRIPTION}
										isSelected={viewModel.isSystemRoleSelected}
										objects={props.systemRoles}
										onActivate={viewModel.onSystemRoleActivated}
										onDeactivate={viewModel.onSystemRoleDeactivated}
									/>
								</div>
							)}
					</div>
					{props.showDownloadHistory && !viewModel.needsCollapse && (
						<SystemDownloadHistory
							systemDownloadHistory={viewModel.system.DOWNLOAD_HISTORY}
						/>
					)}
				</div>
				{!viewModel.needsCollapse && (
					<>
						<div className={styles.systemRolesSection}>
							<h4 className={styles.headline}>
								{t(texts.systemCard.systemRoles)}
							</h4>
							<div className={styles.systemRoles}>
								<MultiSelectionDropDown
									editMode={viewModel.editMode}
									getName={(systemRole) => systemRole.DESCRIPTION}
									getInternalId={(systemRole) => systemRole.SYSTEM_ROLE_ID}
									getObjectId={(systemRole) => systemRole.OBJECT_ID}
									objects={props.systemRoles}
									onUpdate={viewModel.onSystemRolesUpdate}
									isSelected={viewModel.isSystemRoleSelected}
								/>
							</div>
						</div>
						{showSystemRequirements && (
							<>
								<h4 className={styles.headline}>
									{t(texts.systemCard.systemRequirements)}
								</h4>
								<div>
									<SystemRequirementTypeList
										editMode={viewModel.editMode}
										hasSelectedSystemRequirement={
											viewModel.hasSelectedSystemRequirement
										}
										isSystemRequirementSelected={
											viewModel.isSystemRequirementSelected
										}
										systemRequirementTypes={props.systemRequirementTypes}
										onSystemRequirementChanged={
											viewModel.onSystemRequirementChanged
										}
										activeRequirements={viewModel.system.SYSTEM_REQMTS}
										highlightMissingSelection={viewModel.isSystemRequirementSelectionInvalid()}
									/>
								</div>
							</>
						)}
						{viewModel.displayConflicts && (
							<>
								<h4>{t(texts.systemCard.conflicts)}</h4>
								<div className={styles.conflictSection}>
									<ConflictList conflicts={viewModel.system.CONFLICTS} />
								</div>
							</>
						)}
					</>
				)}
			</div>
		</EditableCard>
	);
};
